import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickStartGuideExampleImage from "@/features/pathpick/features/start-guide/components/PathPickStartGuideExampleImage";
import PathPickStartGuideMenuPath from "@/features/pathpick/features/start-guide/components/setup/PathPickStartGuideMenuPath";
import { START_GUIDE_IMAGES } from "@/features/pathpick/features/start-guide/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const KEY = "path_pick_guide:kream_upload_guide.";

// 제공되는 두 파일 — 본문 맨 위에서 무엇을 받았는지부터 알린다.
const FILE_KEYS = ["files_bulk_bidding", "files_disclosure"] as const;

// 업로드 순서. 고시 등록이 먼저이고 대량 입찰이 나중이다 — 뒤집으면 KREAM 이 입찰을 받지 않는다.
const STEPS = [
  { key: "step_1", image: START_GUIDE_IMAGES.kreamMandatoryProductDisclosure },
  { key: "step_2", image: START_GUIDE_IMAGES.kreamBulkBidding },
] as const;

/**
 * KREAM 엑셀 업로드 안내.
 *
 * 등록 신청 내역에서 KREAM 파일 다운로드를 누르면 열린다. KREAM 은 파일이 두 종류이고
 * 올리는 자리도 메뉴가 서로 달라, 단계마다 경로와 캡처를 짝지어 보여준다.
 * 운영의 `PathPickKreamBidGuideModal` 이 따로 알리던 입찰가 입력 안내도
 * 여기 한 줄로 흡수해, 다운로드 한 번에 모달이 둘 뜨지 않게 했다.
 */
function PathPickStartGuideKreamUploadGuideModal({ isOpen, onClose }: Props) {
  const { t } = useI18nHelper();
  const menuPathLabel = t(`${KEY}menu_path_label`);

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      minWidth={"min(960px, 92vw)"}
      maxWidth={"92vw"}
      maxHeight={"92vh"}
      scrollBehavior={"inside"}
      isCentered={true}
    >
      <CustomApprovalModal.Header onClose={onClose} title={t(`${KEY}title`)} />
      <CustomApprovalModal.Body overflow={"auto"}>
        <Flex flexDirection={"column"} gap={"20px"}>
          <Flex
            flexDirection={"column"}
            gap={"6px"}
            padding={"12px"}
            borderRadius={"4px"}
            border={"0.5px solid #E5E5E3"}
          >
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {t(`${KEY}files_title`)}
            </Text>
            {FILE_KEYS.map((fileKey) => (
              <Text
                key={fileKey}
                textStyle={"body-sm-medium"}
                color={"#444440"}
              >
                {`· ${t(`${KEY}${fileKey}`)}`}
              </Text>
            ))}
          </Flex>

          <Text
            textStyle={"body-sm-medium"}
            color={"#6F6F6C"}
            whiteSpace={"pre-line"}
          >
            {t(`${KEY}intro`)}
          </Text>

          {STEPS.map((step) => (
            <Flex key={step.key} flexDirection={"column"} gap={"10px"}>
              <Text textStyle={"body-sm-bold"} color={"#22221F"}>
                {t(`${KEY}${step.key}_title`)}
              </Text>
              <PathPickStartGuideMenuPath
                label={menuPathLabel}
                path={t(`${KEY}${step.key}_menu_path`)}
              />
              <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
                {t(`${KEY}${step.key}_description`)}
              </Text>
              <PathPickStartGuideExampleImage
                src={step.image}
                alt={t(`${KEY}${step.key}_image_alt`)}
                placeholderHeight={"320px"}
              />
            </Flex>
          ))}

          <Flex flexDirection={"column"} gap={"8px"}>
            <PathPickInlineNotice tone={"blue"} leadingIcon={"info-circle"}>
              {t(`${KEY}bid_price_notice`)}
            </PathPickInlineNotice>
            <PathPickInlineNotice tone={"gray"} leadingIcon={"info-circle"}>
              {t(`${KEY}notice`)}
            </PathPickInlineNotice>
          </Flex>

          <Flex justifyContent={"flex-end"}>
            <AppButton variant={"primary"} onClick={onClose}>
              {t("path_pick_guide:close")}
            </AppButton>
          </Flex>
        </Flex>
      </CustomApprovalModal.Body>
    </CustomApprovalModal>
  );
}

export default PathPickStartGuideKreamUploadGuideModal;
